import React, { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { SubmitHandler } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { useFieldValidation } from '../TripItems/Common/Fields/config'
import RHFInput from '../TripItems/Common/Fields/RHFInput'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidTimesCircleIcon from 'LuxKit/Icons/solid/SolidTimesCircleIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useCreateTrip } from 'tripPlanner/hooks/api/trip'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { FullTrip } from 'tripPlanner/types/common'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { itemToPublicTripsBookmarkPayload } from 'tripPlanner/utils/publicTrips/itemToPublicTripBookmarkPayload'
import { itemToPublicTripItemPayload } from 'tripPlanner/utils/publicTrips/itemToPublicTripItemPayload'

type FormModel = {
  tripName: string
}

const ClearIcon = styled(SolidTimesCircleIcon)`
  cursor: pointer;
`

interface Props {
  item: TripItem
  trip: FullTrip | PublicFullTrip
}

export default function NameTripModalView({ item, trip }: Props) {
  const { required } = useFieldValidation()
  const { closeModal, goBack } = useModalCallbacks()
  const intl = useIntl()

  const methods = useForm<FormModel>({
    defaultValues: {
      tripName: trip.name,
    },
  })
  const {
    handleSubmit,
    getValues,
    register,
    setError,
    reset,
    watch,
    setFocus,
  } = methods
  const tripName = watch('tripName')

  const clearFields = useCallback(() => {
    reset({
      tripName: '',
    })
  }, [reset])

  const { showSaveErrorSnackbar, showSaveSuccessSnackbar } =
    useBookmarkSnackbarHandlers()

  useEffect(() => {
    setFocus('tripName')
  }, [setFocus])

  const { mutate: createTrip, isLoading: isCreatingTrip } = useCreateTrip({
    onSuccess: (trip) => saveItem(trip),
    onError: (err) => {
      setError('tripName', {
        message: err.message,
      })
    },
  })

  const createNewTrip: SubmitHandler<FormModel> = useCallback(
    (data) => {
      createTrip({ name: data.tripName, imageId: trip.imageId })
    },
    [createTrip, trip.imageId],
  )

  const { mutate: processBookmarks, isLoading: isProcessingBookmarks } =
    useProcessBookmarks({
      onSuccess: (res, _, trip) => {
        if (trip) {
          showSaveSuccessSnackbar(trip.id, trip.name, res.created[0].id)
        }

        closeModal()
      },
      onError: () => showSaveErrorSnackbar(getValues('tripName')),
    })

  const { mutate: createCustomItem, isLoading: isCreatingItems } =
    useCreateTripItem({
      onSuccess: (item, vars) => {
        showSaveSuccessSnackbar(vars.tripId, getValues('tripName'), item.id)
        closeModal()
      },
    })

  const saveItem = useCallback(
    (trip: FullTrip) => {
      if (isLeBookmark(item)) {
        processBookmarks({
          items: [itemToPublicTripsBookmarkPayload(item)],
          tripIdsAdded: [trip.id],
          tripIdsRemoved: [],
        })
      } else {
        createCustomItem({
          tripId: trip.id,
          tripItem: itemToPublicTripItemPayload(item),
        })
      }
    },
    [item, processBookmarks, createCustomItem],
  )

  // ESLint doesn't like multi-line ternaries with direct JSX
  const rightIcon = useMemo(
    () => <ClearIcon onClick={clearFields} size="M" colour="neutral-four" />,
    [clearFields],
  )

  const isLoading = isProcessingBookmarks || isCreatingItems || isCreatingTrip

  const onSubmit = useMemo(
    () => handleSubmit(createNewTrip),
    [createNewTrip, handleSubmit],
  )

  const createNewTripTitle = intl.formatMessage({
    id: 'y4gOA/lD+8',
    defaultMessage: 'Create new trip',
    description: 'Title for the create new trip modal',
  })
  const tripNameDescription = intl.formatMessage({
    id: 'LmV/HhtP9t',
    defaultMessage: 'You can either keep the current trip name or change it to something else.',
    description: 'Instruction text explaining that the user can rename the trip',
  })
  const tripNameMaxLengthError = intl.formatMessage({
    id: 'TuVIUmWVPG',
    defaultMessage: 'Trip name must be less than 50 characters.',
    description: 'Validation error when trip name exceeds the character limit',
  })
  const nameYourTripLabel = intl.formatMessage({
    id: '8U0rq/n1Sb',
    defaultMessage: 'Name your trip',
    description: 'Label for the trip name input field',
  })
  const createTripButton = intl.formatMessage({
    id: 'dk8YYtZ7pq',
    defaultMessage: 'Create trip',
    description: 'Button text to create a new trip',
  })

  return (
    <>
      <ModalHeader
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
        title={createNewTripTitle}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <VerticalSpacer gap={24}>
              <BodyText colour="neutral-three" variant="medium">
                {tripNameDescription}
              </BodyText>
              <form onSubmit={onSubmit}>
                <RHFInput
                  {...register('tripName', {
                    required,
                    maxLength: {
                      message: tripNameMaxLengthError,
                      value: MAX_TRIP_NAME_CHARS,
                    },
                  })}
                  label={nameYourTripLabel}
                  endIcon={tripName.length > 0 ? rightIcon : undefined}
                  required
                />
              </form>
            </VerticalSpacer>
          </ModalContent>
          <ModalFooter
            primaryActionProps={{
              children: createTripButton,
              onClick: onSubmit,
              disabled: isLoading,
            }}
          />
        </ModalBody>
      </FormProvider>
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}
